import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  CalendarIcon,
  Plus,
  X,
  Flag,
  List,
  AlignLeft,
  CheckSquare,
  Tag,
} from "lucide-react";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${window.location.origin}/api`;

const statusTags = [
  { value: "urgent-important", label: "Urgent and Important", color: "bg-red-500" },
  { value: "not-urgent-important", label: "Not Urgent & Important", color: "bg-blue-500" },
  { value: "urgent-not-important", label: "Urgent Not Important", color: "bg-yellow-500" },
  { value: "in-process", label: "In Process", color: "bg-green-500" },
];

export default function TaskModal({ open, onOpenChange, task, lists, boards, defaultBoard, defaultListId, onSave }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [listId, setListId] = useState("");
  const [priority, setPriority] = useState("medium");
  const [statusTag, setStatusTag] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [subtasks, setSubtasks] = useState([]);
  const [newSubtask, setNewSubtask] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [showSubtasks, setShowSubtasks] = useState(false);

  useEffect(() => {
    if (task) {
      setTitle(task.title || "");
      setDescription(task.description || "");
      setListId(task.list_id || "");
      setPriority(task.priority || "medium");
      setDueDate(task.due_date ? new Date(task.due_date) : null);
      setSubtasks(task.subtasks || []);
      setShowDescription(!!task.description);
      setShowSubtasks((task.subtasks || []).length > 0);
    } else {
      setTitle("");
      setDescription("");
      setListId(defaultListId || "");
      // Map board to priority
      if (defaultBoard === "urgent-important") {
        setPriority("high");
        setStatusTag("urgent-important");
      } else if (defaultBoard === "followups" || defaultBoard === "important-regular") {
        setPriority("medium");
      } else {
        setPriority("low");
      }
      setDueDate(null);
      setSubtasks([]);
      setShowDescription(false);
      setShowSubtasks(false);
    }
  }, [task, defaultListId, defaultBoard, open]);

  const handleAddSubtask = () => {
    if (!newSubtask.trim()) return;
    setSubtasks((prev) => [
      ...prev,
      { id: `st_${Date.now()}`, title: newSubtask.trim(), completed: false },
    ]);
    setNewSubtask("");
  };

  const handleRemoveSubtask = (id) => {
    setSubtasks((prev) => prev.filter((s) => s.id !== id));
  };

  const handleToggleSubtask = (id) => {
    setSubtasks((prev) =>
      prev.map((s) => (s.id === id ? { ...s, completed: !s.completed } : s))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Please enter a task title");
      return;
    }
    if (!listId) {
      toast.error("Please select a list");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        list_id: listId,
        priority,
        due_date: dueDate ? dueDate.toISOString() : null,
        subtasks,
      };

      const url = task ? `${API}/tasks/${task.task_id}` : `${API}/tasks`;
      const method = task ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const savedTask = await response.json();
        onSave(savedTask);
        onOpenChange(false);
        toast.success(task ? "Task updated" : "Task created");
      } else {
        throw new Error("Failed to save task");
      }
    } catch (error) {
      toast.error("Failed to save task");
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (p) => {
    switch (p) {
      case "high": return "text-red-500";
      case "medium": return "text-yellow-500";
      case "low": return "text-blue-500";
      default: return "text-gray-400";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[#2d3136] border-white/10 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{task ? "Edit Task" : "New Task"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What do you want to do?"
            className="text-base border-0 border-b border-white/20 rounded-none px-0 bg-transparent focus-visible:ring-0 text-white placeholder:text-gray-500"
            data-testid="task-title-input"
            autoFocus
          />

          {/* Quick Options Row */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* List Select */}
            <Select value={listId} onValueChange={setListId}>
              <SelectTrigger className="w-auto h-8 text-sm border-dashed border-white/20 bg-transparent text-white" data-testid="task-list-select">
                <List className="w-4 h-4 mr-1" />
                <SelectValue placeholder="List" />
              </SelectTrigger>
              <SelectContent className="bg-[#2d3136] border-white/10">
                {lists.map((list) => (
                  <SelectItem key={list.list_id} value={list.list_id} className="text-white">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: list.color }} />
                      {list.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Due Date */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn("h-8 text-sm border-dashed border-white/20 bg-transparent text-white", dueDate && "text-blue-400")}
                  data-testid="task-due-date-btn"
                >
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  {dueDate ? format(dueDate, "MMM d") : "Date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#2d3136] border-white/10" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  initialFocus
                  className="bg-[#2d3136] text-white"
                />
                {dueDate && (
                  <div className="p-2 border-t border-white/10">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full text-white"
                      onClick={() => setDueDate(null)}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>

            {/* Priority */}
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="w-auto h-8 text-sm border-dashed border-white/20 bg-transparent text-white" data-testid="task-priority-select">
                <Flag className={cn("w-4 h-4 mr-1", getPriorityColor(priority))} />
                <span className="capitalize">{priority}</span>
              </SelectTrigger>
              <SelectContent className="bg-[#2d3136] border-white/10">
                <SelectItem value="high" className="text-white">
                  <div className="flex items-center gap-2">
                    <Flag className="w-4 h-4 text-red-500" />
                    High
                  </div>
                </SelectItem>
                <SelectItem value="medium" className="text-white">
                  <div className="flex items-center gap-2">
                    <Flag className="w-4 h-4 text-yellow-500" />
                    Medium
                  </div>
                </SelectItem>
                <SelectItem value="low" className="text-white">
                  <div className="flex items-center gap-2">
                    <Flag className="w-4 h-4 text-blue-500" />
                    Low
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Status Tag */}
            <Select value={statusTag} onValueChange={setStatusTag}>
              <SelectTrigger className="w-auto h-8 text-sm border-dashed border-white/20 bg-transparent text-white">
                <Tag className="w-4 h-4 mr-1" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#2d3136] border-white/10">
                {statusTags.map((tag) => (
                  <SelectItem key={tag.value} value={tag.value} className="text-white">
                    <div className="flex items-center gap-2">
                      <div className={cn("w-2 h-2 rounded-full", tag.color)} />
                      {tag.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Toggle Buttons */}
          <div className="flex gap-2">
            {!showDescription && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowDescription(true)}
                className="text-gray-400 hover:text-white"
              >
                <AlignLeft className="w-4 h-4 mr-1" />
                Add note
              </Button>
            )}
            {!showSubtasks && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowSubtasks(true)}
                className="text-gray-400 hover:text-white"
              >
                <CheckSquare className="w-4 h-4 mr-1" />
                Add subtasks
              </Button>
            )}
          </div>

          {/* Description */}
          {showDescription && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Note</span>
                <button
                  type="button"
                  onClick={() => { setShowDescription(false); setDescription(""); }}
                  className="p-1 hover:bg-white/10 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add details..."
                className="min-h-[80px] resize-none bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                data-testid="task-description-input"
              />
            </div>
          )}

          {/* Subtasks */}
          {showSubtasks && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Subtasks</span>
                <button
                  type="button"
                  onClick={() => { setShowSubtasks(false); setSubtasks([]); }}
                  className="p-1 hover:bg-white/10 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              {subtasks.map((subtask) => (
                <div key={subtask.id} className="flex items-center gap-2 group">
                  <Checkbox
                    checked={subtask.completed}
                    onCheckedChange={() => handleToggleSubtask(subtask.id)}
                    className="h-4 w-4 border-white/30"
                  />
                  <span className={cn("flex-1 text-sm", subtask.completed && "line-through text-gray-500")}>
                    {subtask.title}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveSubtask(subtask.id)}
                    className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}

              <div className="flex gap-2">
                <Input
                  value={newSubtask}
                  onChange={(e) => setNewSubtask(e.target.value)}
                  placeholder="Add subtask..."
                  className="h-8 text-sm bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddSubtask();
                    }
                  }}
                  data-testid="subtask-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddSubtask}
                  className="h-8 border-white/20"
                  data-testid="add-subtask-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2 sticky bottom-0 bg-[#2d3136] pb-1">
            <Button
              type="button"
              variant="outline"
              className="flex-1 border-white/20 text-white hover:bg-white/10"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-task-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              disabled={loading}
              data-testid="save-task-btn"
            >
              {loading ? "Saving..." : task ? "Save" : "Add Task"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
